import React, { useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  LocalShipping as DispatchedIcon,
  CheckCircle as CollectedIcon,
  Replay as ReturnedIcon,
  HowToReg as WalkinIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FileDownloadOutlined as ExportIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  EasyCheckButton,
  EasyCheckDataGrid,
  EasyCheckChip,
  EasyCheckTextField,
  EasyCheckSelect,
  EasyCheckDatePicker,
} from '../../common/Controls';
import { chequeCollectionService } from '../../../services/chequeCollection/chequeCollectionService';
import { exportToExcel } from '../../../utils/excelExport';
import { formatCurrencyAmount, getCurrencyDecimals } from '../../../utils/currencyUtils';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import WalkinCollectionDialog from './WalkinCollectionDialog';
import CourierDispatchDialog from './CourierDispatchDialog';
import DeliveryConfirmDialog from './DeliveryConfirmDialog';
import ReturnDialog from './ReturnDialog';
import CollectionDetailDialog from './CollectionDetailDialog';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: '#F7F9FC',
}));


const STATUS_COLORS = {
  Awaiting: { bg: '#FFF7ED', color: '#C2410C' },
  Dispatched: { bg: '#EFF6FF', color: '#1D4ED8' },
  Collected: { bg: '#F0FDF4', color: '#15803D' },
  Returned: { bg: '#FEF2F2', color: '#B91C1C' },
};

const METHOD_COLORS = {
  'Walk-in': { bg: '#F5F3FF', color: '#7C3AED' },
  Courier: { bg: '#EFF6FF', color: '#1D4ED8' },
};

const ChequeCollectionPage = () => {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Dialog state
  const [activeDialog, setActiveDialog] = useState({ type: null, cheque: null });

  // Collections list
  const queryParams = {
    pageNumber: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(dateFrom && { dateFrom: dateFrom.toISOString().split('T')[0] }),
    ...(dateTo && { dateTo: dateTo.toISOString().split('T')[0] }),
  };

  const { data: collectionsResult, isLoading, refetch } = useQuery({
    queryKey: ['chequeCollections', queryParams],
    queryFn: () => chequeCollectionService.getCollections(queryParams),
  });

  const collections = collectionsResult?.data || [];
  const totalCount = collectionsResult?.totalCount || 0;

  const handleDialogClose = useCallback(() => {
    setActiveDialog({ type: null, cheque: null });
  }, []);

  const handleActionSuccess = useCallback((message) => {
    showSuccess(message);
    handleDialogClose();
    refetch();
  }, [showSuccess, handleDialogClose, refetch]);

  const handleActionError = useCallback((message) => {
    showError(message);
  }, [showError]);

  const formatCurrency = (amount, currency) => {
    const sym = currency || 'AED';
    return `${sym} ${formatCurrencyAmount(amount, currency, { withSymbol: false })}`;
  };

  const [exporting, setExporting] = useState(false);

  const mapRowForExport = (row) => ({
    chequeNumber: row.ChequeNumber || row.chequeNumber || '',
    beneficiary: row.BeneficiaryName || row.beneficiaryName || '',
    amount: row.ChqAmount ?? row.chqAmount ?? 0,
    currency: row.Currency || row.currency || '',
    bank: row.BankName || row.bankName || '',
    chequeDate: row.ChqDate || row.chqDate ? new Date(row.ChqDate || row.chqDate).toLocaleDateString() : '',
    method: row.DeliveryMethod || row.deliveryMethod || '',
    status: row.CollectionStatus || row.collectionStatus || '',
    collectionDate: row.CollectionDate || row.collectionDate ? new Date(row.CollectionDate || row.collectionDate).toLocaleDateString() : '',
    collectorCourier: row.CollectorName || row.collectorName || row.CourierCompany || row.courierCompany || '',
    remarks: row.Remarks || row.remarks || '',
  });

  const handleExport = async () => {
    if (!totalCount) {
      showError('No data to export');
      return;
    }
    const exportColumns = [
      { header: 'Cheque No.', key: 'chequeNumber', type: 'text' },
      { header: 'Beneficiary', key: 'beneficiary', type: 'text' },
      { header: 'Amount', key: 'amount', type: 'amount' },
      { header: 'Currency', key: 'currency', type: 'text' },
      { header: 'Bank', key: 'bank', type: 'text' },
      { header: 'Cheque Date', key: 'chequeDate', type: 'date' },
      { header: 'Method', key: 'method', type: 'text' },
      { header: 'Status', key: 'status', type: 'text' },
      { header: 'Collection Date', key: 'collectionDate', type: 'date' },
      { header: 'Collector / Courier', key: 'collectorCourier', type: 'text' },
      { header: 'Remarks', key: 'remarks', type: 'text' },
    ];

    setExporting(true);
    try {
      // Fetch all records with current filters (no pagination limit)
      const allParams = {
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(dateFrom && { dateFrom: dateFrom.toISOString().split('T')[0] }),
        ...(dateTo && { dateTo: dateTo.toISOString().split('T')[0] }),
        pageNumber: 1,
        pageSize: 10000,
      };
      const result = await chequeCollectionService.getCollections(allParams);
      const allRows = result?.data || [];
      if (!allRows.length) {
        showError('No data to export');
        return;
      }
      const exportData = allRows.map(mapRowForExport);

      // Use the single currency's decimals for the amount column, else 2 (mixed).
      const exportCurrencies = new Set(exportData.map((r) => r.currency || 'AED'));
      const amountCol = exportColumns.find((c) => c.key === 'amount');
      if (amountCol) {
        amountCol.decimals = exportCurrencies.size === 1 ? getCurrencyDecimals([...exportCurrencies][0]) : 2;
      }

      const parameters = [];
      if (statusFilter) parameters.push({ label: 'Status', value: statusFilter });
      if (dateFrom) parameters.push({ label: 'From', value: dateFrom.toLocaleDateString() });
      if (dateTo) parameters.push({ label: 'To', value: dateTo.toLocaleDateString() });
      if (search) parameters.push({ label: 'Search', value: search });

      exportToExcel({
        title: 'Cheque Collection Report',
        fileName: `Cheque_Collection_${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Collections',
        columns: exportColumns,
        data: exportData,
        parameters,
      });
    } catch (err) {
      showError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      field: 'ChequeNumber',
      headerName: 'Cheque No.',
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => row.ChequeNumber || row.chequeNumber || '',
    },
    {
      field: 'BeneficiaryName',
      headerName: 'Beneficiary',
      flex: 1.5,
      minWidth: 160,
      valueGetter: (value, row) => row.BeneficiaryName || row.beneficiaryName || '',
    },
    {
      field: 'ChqAmount',
      headerName: 'Amount',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const amount = params.row.ChqAmount ?? params.row.chqAmount ?? 0;
        const currency = params.row.Currency || params.row.currency || '';
        return <Typography variant="body2" fontWeight={600}>{formatCurrency(amount, currency)}</Typography>;
      },
    },
    {
      field: 'BankName',
      headerName: 'Bank',
      flex: 1,
      minWidth: 140,
      valueGetter: (value, row) => row.BankName || row.bankName || '',
    },
    {
      field: 'ChqDate',
      headerName: 'Cheque Date',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (value, row) => {
        const d = row.ChqDate || row.chqDate;
        return d ? new Date(d).toLocaleDateString() : '';
      },
    },
    {
      field: 'DeliveryMethod',
      headerName: 'Method',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => {
        const method = params.row.DeliveryMethod || params.row.deliveryMethod;
        if (!method) return null;
        const colors = METHOD_COLORS[method] || {};
        return (
          <EasyCheckChip
            label={method}
            size="small"
            sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'CollectionStatus',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => {
        const status = params.row.CollectionStatus || params.row.collectionStatus || 'Awaiting';
        const colors = STATUS_COLORS[status] || {};
        return (
          <EasyCheckChip
            label={status}
            size="small"
            sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const status = params.row.CollectionStatus || params.row.collectionStatus || 'Awaiting';
        const cheque = params.row;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {status === 'Awaiting' && (
              <>
                <Tooltip title="Mark Walk-in Collected">
                  <IconButton size="small" color="success" onClick={() => setActiveDialog({ type: 'walkin', cheque })}>
                    <WalkinIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Dispatch via Courier">
                  <IconButton size="small" color="primary" onClick={() => setActiveDialog({ type: 'dispatch', cheque })}>
                    <DispatchedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {status === 'Dispatched' && (
              <>
                <Tooltip title="Confirm Delivery">
                  <IconButton size="small" color="success" onClick={() => setActiveDialog({ type: 'deliver', cheque })}>
                    <CollectedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Mark Returned">
                  <IconButton size="small" color="error" onClick={() => setActiveDialog({ type: 'return', cheque })}>
                    <ReturnedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {(status === 'Collected' || status === 'Returned') && (
              <Tooltip title="View Details">
                <IconButton size="small" onClick={() => setActiveDialog({ type: 'detail', cheque })}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(status === 'Awaiting' || status === 'Dispatched') && (
              <Tooltip title="View Details">
                <IconButton size="small" onClick={() => setActiveDialog({ type: 'detail', cheque })}>
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const getRowId = (row) => row.ChequeId || row.chequeId || row.Id || row.id;

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Cheque Collection
        </Typography>
        <EasyCheckButton
          buttonType="action"
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={!totalCount || exporting}
          loading={exporting}
        >
          Export to Excel
        </EasyCheckButton>
      </Box>

      {/* Filter Bar */}
      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch' }}>
        <Grid item xs={12} md={6}>
          <EasyCheckTextField
            variant="search"
            placeholder="Search cheque no. or beneficiary..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <EasyCheckSelect
            variant="search"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            fullWidth
            size="small"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="awaiting">Awaiting</MenuItem>
            <MenuItem value="dispatched">Dispatched</MenuItem>
            <MenuItem value="collected">Collected</MenuItem>
            <MenuItem value="returned">Returned</MenuItem>
          </EasyCheckSelect>
        </Grid>
        <Grid item xs={6} md={2}>
          <EasyCheckDatePicker
            label="From"
            value={dateFrom}
            onChange={setDateFrom}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <EasyCheckDatePicker
            label="To"
            value={dateTo}
            onChange={setDateTo}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <EasyCheckDataGrid
            rows={collections}
            columns={columns}
            getRowId={getRowId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            autoHeight={false}
            sx={{ height: '100%' }}
          />
        )}
      </Box>

      {/* Dialogs */}
      {activeDialog.type === 'walkin' && (
        <WalkinCollectionDialog
          open
          cheque={activeDialog.cheque}
          onClose={handleDialogClose}
          onSuccess={handleActionSuccess}
          onError={handleActionError}
        />
      )}
      {activeDialog.type === 'dispatch' && (
        <CourierDispatchDialog
          open
          cheque={activeDialog.cheque}
          onClose={handleDialogClose}
          onSuccess={handleActionSuccess}
          onError={handleActionError}
        />
      )}
      {activeDialog.type === 'deliver' && (
        <DeliveryConfirmDialog
          open
          cheque={activeDialog.cheque}
          onClose={handleDialogClose}
          onSuccess={handleActionSuccess}
          onError={handleActionError}
        />
      )}
      {activeDialog.type === 'return' && (
        <ReturnDialog
          open
          cheque={activeDialog.cheque}
          onClose={handleDialogClose}
          onSuccess={handleActionSuccess}
          onError={handleActionError}
        />
      )}
      {activeDialog.type === 'detail' && (
        <CollectionDetailDialog
          open
          cheque={activeDialog.cheque}
          onClose={handleDialogClose}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </PageContainer>
  );
};

export default ChequeCollectionPage;
